<script>
    // Exactly one `primary` per screen. If a screen seems to need two, one of
    // them is a `secondary`.
    //
    //   <Button on:click={go}>Analyze my skin</Button>
    //   <Button variant="secondary" href="/routine">See my routine</Button>
    //   <Button variant="ghost" size="sm" on:click={reset}>Start over</Button>
    //
    // Every variant is an outline that fills on hover. That is the app's
    // original editorial treatment; hierarchy comes from colour, not from
    // weight. `primary` carries the rose accent, `secondary` a neutral rule,
    // `ghost` no rule at all.
    //
    // The old `label` / `onClick` API still works, so existing callers keep
    // rendering while screens are migrated:
    //
    //   <Button label="Back" onClick={goBack} />

    export let variant = 'primary'; // primary | secondary | ghost | danger
    export let size = 'md';         // sm | md | lg
    export let type = 'button';
    export let href = null;
    export let disabled = false;
    export let loading = false;
    export let full = false;
    export let busyLabel = 'Working';
    export let label = null;        // legacy
    export let onClick = null;      // legacy

    $: isDisabled = disabled || loading;

    // `onClick` is the legacy prop. Wired on BOTH branches so an <a> behaves
    // the same as a <button>; it used to be silently dropped when href was set.
    function handleClick(event) {
        if (typeof onClick === 'function') onClick(event);
    }
</script>

<style>
    .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        text-align: center;
        background: transparent;
        border: 1px solid transparent;
        border-radius: var(--radius-0);
        cursor: pointer;
        margin: 0;
        white-space: nowrap;
        transition:
            background var(--transition),
            border-color var(--transition),
            color var(--transition),
            opacity var(--transition);
    }

    .full { width: 100%; }

    /* --- size ----------------------------------------------------------- */
    .sm { padding: var(--space-2) var(--space-5); font-size: var(--text-3xs); }
    .md { padding: var(--space-4) var(--space-10); font-size: var(--text-xs); }
    .lg { padding: var(--space-5) var(--space-12); font-size: var(--text-sm); }

    /* --- variant: outline at rest, fills on hover ------------------------ */
    .primary {
        border-color: var(--rose);
        color: var(--rose);
    }
    .primary:hover:not(.is-disabled) {
        background: var(--rose);
        color: var(--bg);
    }

    .secondary {
        border-color: var(--border-strong);
        color: var(--cream);
    }
    .secondary:hover:not(.is-disabled) {
        background: var(--hover-veil);
        border-color: var(--cream);
    }

    .ghost {
        border-color: transparent;
        color: var(--text-muted);
    }
    .ghost:hover:not(.is-disabled) { color: var(--cream); }

    /* Compound selectors, so ghost's inset does not depend on this file's
       source order relative to the size classes. */
    .ghost.sm { padding-left: var(--space-1); padding-right: var(--space-1); }
    .ghost.md { padding-left: var(--space-2); padding-right: var(--space-2); }
    .ghost.lg { padding-left: var(--space-3); padding-right: var(--space-3); }

    .danger {
        border-color: var(--danger);
        color: var(--danger);
    }
    .danger:hover:not(.is-disabled) {
        background: var(--danger);
        color: var(--bg);
    }

    .is-disabled {
        opacity: 0.45;
        cursor: not-allowed;
        pointer-events: none;
    }

    .spinner {
        width: 1em;
        height: 1em;
        border: 1.5px solid currentColor;
        border-top-color: transparent;
        border-radius: var(--radius-circle);
        animation: spin 700ms linear infinite;
        flex: none;
    }

    @keyframes spin { to { transform: rotate(360deg); } }

    @media (prefers-reduced-motion: reduce) {
        .spinner { animation: none; opacity: 0.6; }
    }
</style>

{#if href}
    <a
        {...$$restProps}
        class="btn {variant} {size}"
        class:full
        class:is-disabled={isDisabled}
        href={isDisabled ? undefined : href}
        aria-disabled={isDisabled}
        aria-busy={loading}
        on:click
        on:click={handleClick}
    >
        {#if loading}<span class="spinner" aria-hidden="true"></span>{/if}
        <slot>{label}</slot>
        <!-- The spinner is decorative, so the busy state needs its own text. -->
        {#if loading}<span class="u-visually-hidden">{busyLabel}</span>{/if}
    </a>
{:else}
    <button
        {...$$restProps}
        class="btn {variant} {size}"
        class:full
        class:is-disabled={isDisabled}
        {type}
        disabled={isDisabled}
        aria-busy={loading}
        on:click
        on:click={handleClick}
    >
        {#if loading}<span class="spinner" aria-hidden="true"></span>{/if}
        <slot>{label}</slot>
        {#if loading}<span class="u-visually-hidden">{busyLabel}</span>{/if}
    </button>
{/if}
